/**
 * Tela cheia de um relatorio (spec 0009, rodadas 2 e 3).
 *
 * O tipo do relatorio decide o renderizador: `pbi` embute pelo `powerbi-client` com a concessao que
 * o backend emite; `inteli_graph` embute a URL de render do NIG num iframe (rodada 3).
 *
 * A tela nao decide acesso. Ela pede a concessao; quem resolve policy, identidade e RLS e o
 * backend - e quem registra a auditoria tambem.
 *
 * TELA CHEIA (spec 0003, R4): sem sidebar e sem catalogo, so uma barra minima e o relatorio com a
 * janela inteira. A barra tem o desenho da barra superior do Auth Manager - o mesmo fundo, o
 * sobretitulo em `--color-tech` -, para a leitura continuar parecendo o mesmo produto.
 */

import { Button, StatusBadge } from '@ledger-glass/react'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { ApiError } from '../api/client'
import { fetchEmbedToken, fetchIntelGraphEmbed, listReports } from '../api/reports'
import type { PowerBiErrorDetail, ReportListItem } from '../api/types'
import { grupoDe } from '../catalog/workspaces'
import { Alert } from '../components/Alert'
import { Spinner } from '../components/Spinner'
import { usePowerbiEmbed } from '../powerbi/useEmbed'

export function ReportView({ reportId, onClose }: { reportId: string; onClose: () => void }) {
  // O catalogo ja esta em cache quando se chega pelo card; num link direto, e buscado agora.
  const catalogo = useQuery({ queryKey: ['reports'], queryFn: listReports })
  const relatorio = (catalogo.data ?? []).find((item) => item.id === reportId) ?? null

  return (
    <div className="leitura-tela">
      <header className="leitura-tela__barra">
        <Button icon="chevronLeft" onClick={onClose} size="small" variant="ghost">
          Catálogo
        </Button>
        <div className="leitura-tela__titulo">
          <p className="eyebrow">{relatorio === null ? 'Relatório' : grupoDe(relatorio)}</p>
          <h1>{relatorio?.name ?? 'Relatório'}</h1>
        </div>
        {relatorio === null ? null : (
          <StatusBadge tone={relatorio.report_type === 'inteli_graph' ? 'tech' : 'neutral'}>
            {relatorio.report_type === 'inteli_graph' ? 'Inteli Graph' : 'Power BI'}
          </StatusBadge>
        )}
      </header>

      <main className="leitura" id="conteudo">
        {catalogo.isPending ? (
          <Spinner label="Abrindo o relatório…" showLabel />
        ) : relatorio === null ? (
          <Alert tone="warning" action={<Button onClick={onClose}>Voltar ao catálogo</Button>}>
            Relatório não encontrado — ou você não tem acesso a ele.
          </Alert>
        ) : relatorio.report_type === 'inteli_graph' ? (
          <IntelGraphReport report={relatorio} />
        ) : (
          <PowerBiReport report={relatorio} />
        )}
      </main>
    </div>
  )
}

function PowerBiReport({ report }: { report: ReportListItem }) {
  const concessao = useQuery({
    queryKey: ['embed-token', report.id],
    queryFn: () => fetchEmbedToken(report.id),
    // O token do PowerBI tem validade curta: nao vale reaproveitar entre aberturas.
    staleTime: 0,
    gcTime: 0,
    retry: false,
  })

  const { container, erro, expirando } = usePowerbiEmbed(concessao.data ?? null)

  if (concessao.isPending) return <Spinner label="Pedindo acesso ao relatório…" showLabel />
  if (concessao.error !== null) return <FalhaDeConcessao erro={concessao.error} />

  return (
    <>
      {expirando ? (
        <Alert
          tone="warning"
          action={<Button onClick={() => void concessao.refetch()}>Renovar</Button>}
        >
          O acesso a este relatório está expirando.
        </Alert>
      ) : null}
      {erro === null ? null : (
        <Alert tone="danger">
          <strong>O Power BI recusou a exibição.</strong>
          <pre className="diagnostico">{erro}</pre>
        </Alert>
      )}
      {/* Diz se o recorte por RLS entrou. Sem isso, "vejo tudo" e "a policy nao casou" sao a
          mesma tela - e foi exatamente a duvida que apareceu nos testes de RLS. */}
      {concessao.data?.rls_applied === true ? (
        <p className="leitura__rls">
          <StatusBadge tone="positive">RLS aplicado</StatusBadge>
          <span>Os dados abaixo estão filtrados para o seu perfil.</span>
        </p>
      ) : null}
      <div className="embed" ref={container} />
    </>
  )
}

/**
 * Relatorio do New Inteli Graph: um iframe com a URL que o backend montou.
 *
 * O NIG tem sessao propria e desenha o painel inteiro; aqui so entregamos o quadro. O token curto
 * ja vem embutido na URL pelo backend - a tela nunca o manuseia.
 */
function IntelGraphReport({ report }: { report: ReportListItem }) {
  const [carregando, setCarregando] = useState(true)
  const embed = useQuery({
    queryKey: ['inteli-graph-embed', report.id],
    queryFn: () => fetchIntelGraphEmbed(report.id),
    // Token curto e escopado a UMA abertura: nao se reaproveita de cache.
    staleTime: 0,
    gcTime: 0,
    retry: false,
  })

  if (embed.isPending) return <Spinner label="Pedindo acesso ao relatório…" showLabel />
  if (embed.error !== null) {
    return (
      <Alert tone="danger" action={<Button onClick={() => void embed.refetch()}>Tentar de novo</Button>}>
        Não foi possível abrir o relatório: {embed.error.message}
      </Alert>
    )
  }

  return (
    <div className="embed embed--frame">
      {carregando ? (
        <div className="embed__loading">
          <Spinner label="Carregando o relatório…" showLabel />
        </div>
      ) : null}
      <iframe
        title={report.name}
        src={embed.data.render_url}
        onLoad={() => setCarregando(false)}
      />
    </div>
  )
}

/**
 * Falha ao PEDIR a concessao (antes de qualquer embed).
 *
 * Quando o PowerBI recusa, o backend devolve um objeto com etapa, codigo, mensagem e os
 * identificadores de rastreio. Mostrar tudo e deliberado: `request_id` e `activity_id` sao o que a
 * Microsoft pede num chamado, e sem eles o diagnostico vira adivinhacao.
 */
function FalhaDeConcessao({ erro }: { erro: Error }) {
  const detalhe = erro instanceof ApiError ? erro.payload : undefined
  const estruturado =
    detalhe !== null && typeof detalhe === 'object' ? (detalhe as PowerBiErrorDetail) : null

  if (estruturado === null) {
    return <Alert tone="danger">Não foi possível abrir o relatório: {erro.message}</Alert>
  }

  return (
    <>
      <Alert tone="danger">
        Não foi possível abrir o relatório:{' '}
        {estruturado.powerbi_error_code ?? estruturado.error ?? 'erro do Power BI'}
      </Alert>
      {estruturado.hint === undefined ? null : <Alert tone="warning">{estruturado.hint}</Alert>}
      <details className="diagnostico-bloco">
        <summary>Detalhes técnicos</summary>
        <dl className="diagnostico-lista">
          <dt>Etapa</dt>
          <dd>{estruturado.stage ?? '—'}</dd>
          <dt>HTTP do Power BI</dt>
          <dd>{estruturado.powerbi_status ?? '—'}</dd>
          <dt>Mensagem</dt>
          <dd>{estruturado.powerbi_message ?? '—'}</dd>
          <dt>RequestId</dt>
          <dd>
            <code>{estruturado.request_id ?? '—'}</code>
          </dd>
          <dt>ActivityId</dt>
          <dd>
            <code>{estruturado.activity_id ?? '—'}</code>
          </dd>
        </dl>
        <pre className="diagnostico">{JSON.stringify(estruturado, null, 2)}</pre>
      </details>
    </>
  )
}
